import React, { useEffect, useState } from "react";
import { Produce, ProduceReportsResponse, ProduceSalesReportResponse } from "../../types/produce.type";
import { ResidueType } from "../../types/residue.type";
import {
  Form,
  Input,
  message,
  Typography,
  Select,
  Space,
  Button,
  Spin,
  Table,
  Modal,
  Menu,
  Dropdown,
  InputNumber,
  Checkbox,
  DatePicker,
  Card,
  Row,
  Col,
  Statistic,
  Progress,
  Tag,
  Divider,
  Tabs,
  Alert,
  Tooltip,
} from "antd";
import { DownOutlined, TrophyOutlined, UserOutlined, BarChartOutlined, RocketOutlined, InfoCircleOutlined, DollarOutlined } from "@ant-design/icons";
import { UnitOfMeasurement } from "../../types/unitOfMeasurement.type";
import { api } from "../../api/api";
import useGetRequest from "../../hooks/useGetRequest";
import { Material } from "../../types/material.type";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../contexts/auth.context";
import { canEditProduces } from "../../utils/roleUtils";
import { Currency } from "../../types/currency.type";
import { useDownloadFile } from "../../hooks/useDownloadFile";
import { PiMicrosoftExcelLogoFill } from "react-icons/pi";
import { formatNumber } from "../../utils/formatNumber";
import { UserRole } from "../../types/user.type";
import dayjs from "dayjs";
import { useExchangeRate } from "../../contexts/exchangeRate.context";

const { Title } = Typography;
const { Search } = Input;
const { Option } = Select;
const { RangePicker } = DatePicker;

export default function Produces() {
  const { authState } = useAuth();
  const navigate = useNavigate();
  const [produces, setProduces] = useState<ResidueType[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [confirmLoading, setConfirmLoading] = useState<boolean>(false);
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [unitFilter, setUnitFilter] = useState<string | undefined>(undefined);
  const [sortBy, setSortBy] = useState<string>("name");
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("asc");
  const [page, setPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(10);
  const [total, setTotal] = useState<number>(0);
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [hasResidue, setHasResidue] = useState<boolean>(false);
  const [belowLimit, setBelowLimit] = useState<boolean>(false);
  const [selectedProduce, setSelectedProduce] = useState<ResidueType | null>(null);
  const [form] = Form.useForm();
  const { data: materialsList, loading: materialLoading } =
    useGetRequest(`/material/list`);
  const { data: producesList, loading: listLoading } =
    useGetRequest(`/produce/list`);
  const { loading: downloadLoading, downloadFile } = useDownloadFile();
  const { exchangeRate } = useExchangeRate();
  // Новые состояния для отчетов
  const [isReportsModalOpen, setIsReportsModalOpen] = useState<boolean>(false);
  const [selectedProduceForReports, setSelectedProduceForReports] = useState<ResidueType | null>(null);
  const [reportsData, setReportsData] = useState<ProduceReportsResponse | null>(null);
  const [reportsLoading, setReportsLoading] = useState<boolean>(false);
  const [reportsDateRange, setReportsDateRange] = useState<[string, string] | null>(null);
  
  // Состояния для аналитики трендов
  const [trendAnalysis, setTrendAnalysis] = useState<any>(null);
  const [predictionData, setPredictionData] = useState<any>(null);

  // Состояния для отчета по продажам
  const [isSalesReportModalOpen, setIsSalesReportModalOpen] = useState<boolean>(false);
  const [selectedProduceForSalesReport, setSelectedProduceForSalesReport] = useState<ResidueType | null>(null);
  const [salesReportData, setSalesReportData] = useState<ProduceSalesReportResponse | null>(null);
  const [salesReportLoading, setSalesReportLoading] = useState<boolean>(false);
  const [salesReportDateRange, setSalesReportDateRange] = useState<[string, string] | null>(null);
  const [salesReportCurrency, setSalesReportCurrency] = useState<Currency>(Currency.US_DOLLAR);
  const [selectedProduces, setSelectedProduces] = useState<string[]>([]);
  const [showHidden, setShowHidden] = useState<boolean>(false);
  const [hideLoading, setHideLoading] = useState<boolean>(false);

  // Состояния для изменения количества кг
  const [isQuantityKgModalOpen, setIsQuantityKgModalOpen] = useState<boolean>(false);
  const [selectedProduceForQuantityKg, setSelectedProduceForQuantityKg] = useState<ResidueType | null>(null);
  const [quantityKgForm] = Form.useForm();
  const [quantityKgLoading, setQuantityKgLoading] = useState<boolean>(false);

  // Состояния для расчета расходников
  const [ingredientsData, setIngredientsData] = useState<any>(null);
  const [targetQuantityForIngredients, setTargetQuantityForIngredients] = useState<number>(0);

  useEffect(() => {
    fetchProduces();
  }, [page, searchTerm, unitFilter, sortBy, sortOrder, pageSize, hasResidue, belowLimit, showHidden]);

  const fetchProduces = async () => {
    setLoading(true);
    try {
      const response = await api.get(`/residue`, {
        params: {
          page,
          type: "produces",
          limit: pageSize,
          search: searchTerm,
          sortBy,
          sortOrder,
          hasResidue: hasResidue ? true : undefined,
          belowLimit: belowLimit ? "true" : undefined,
          unitOfMeasurement: unitFilter,
          showHidden: showHidden ? "true" : undefined,
        },
      });
      setProduces(response.data.data);
      setTotal(response.data.total);
    } catch (error) {
      console.error("Error fetching produces", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (value: string) => {
    setSearchTerm(value);
    setPage(1);
  };

  const handleAddProduce = () => {
    // Сбрасываем состояние формы и выбранной продукции
    form.resetFields();
    setSelectedProduce(null);
    setIsModalOpen(true);
  };

  const handleFormSubmit = async (values: any) => {
    setConfirmLoading(true);
    const materials = values.materials || [];
    const produces = values.produces || [];

    const materialIds = materials.map((material: any) => material.materialId);
    const hasDuplicates = materialIds.some(
      (id: any, index: number) => materialIds.indexOf(id) !== index
    );

    const produceIds = produces.map((produce: any) => produce.produceId);
    const hasProduceDuplicates = produceIds.some(
      (id: any, index: number) => produceIds.indexOf(id) !== index
    );

    if (hasDuplicates || hasProduceDuplicates) {
      message.error(
        "Нельзя использовать один и тот же материал несколько раз."
      );
      setConfirmLoading(false);
      return;
    }
    const materialsWithNumbers = materials.map((material: any) => ({
      ...material,
      quantity: Number(material.quantity),
    }));

    const producesWithNumbers = produces.map((produce: any) => ({
      ...produce,
      quantity: Number(produce.quantity),
    }));

    const updatedValues = {
      ...values,
      materials: materialsWithNumbers,
      produces: producesWithNumbers,
      price: {
        ...values.price,
        exchangeRate: exchangeRate || values.exchangeRate,
      },
    };

    try {
      if (selectedProduce) {
        await api.put(`/produce/${selectedProduce._id}`, updatedValues);
        message.success("Продукция обновлена.");
      } else {
        await api.post(`/produce`, updatedValues);
        message.success("Продукция добавлена.");
      }
      form.resetFields();
      setSelectedProduce(null);
      fetchProduces();
      setIsModalOpen(false);
      setConfirmLoading(false);
    } catch (error: any) {
      setConfirmLoading(false);
      console.error("Error saving produce:", error);
      message.error(error.response.data.error);
    }
  };

  const handleEditProduce = (produce: ResidueType) => {
    const data = {
      ...produce,
      price: {
        amount: produce.price.USD, // Используем USD как основную валюту
        exchangeRate: produce.price.exchangeRate,
        currency: produce.price.currency,
      },
    };
    form.setFieldsValue(data);
    setSelectedProduce(produce);
    setIsModalOpen(true);
  };

  const handleExport = () => {
    downloadFile("/export/residues", "produces_data.xlsx", { type: "produces" });
  };

  // Новые функции для отчетов
  const handleOpenReportsModal = (produce: ResidueType) => {
    setSelectedProduceForReports(produce);
    setIsReportsModalOpen(true);
    setReportsData(null);
    setReportsDateRange(null);
  };

  const handleCloseReportsModal = () => {
    setIsReportsModalOpen(false);
    setSelectedProduceForReports(null);
    setReportsData(null);
    setReportsDateRange(null);
    setTrendAnalysis(null);
    setPredictionData(null);
    setIngredientsData(null);
    setTargetQuantityForIngredients(0);
  };

  // Функция для обновления расчета расходников
  const handleTargetQuantityChange = (value: number | null) => {
    if (value && selectedProduceForReports) {
      setTargetQuantityForIngredients(value);
      const ingredients = calculateIngredients(value, selectedProduceForReports);
      setIngredientsData(ingredients);
    }
  };

  // Функции для отчета по продажам
  const handleOpenSalesReportModal = (produce: ResidueType) => {
    setSelectedProduceForSalesReport(produce);
    setIsSalesReportModalOpen(true);
    setSalesReportData(null);
    setSalesReportDateRange(null);
    setSalesReportCurrency(Currency.US_DOLLAR);
  };

  const handleCloseSalesReportModal = () => {
    setIsSalesReportModalOpen(false);
    setSelectedProduceForSalesReport(null);
    setSalesReportData(null);
    setSalesReportDateRange(null);
    setSalesReportCurrency(Currency.US_DOLLAR);
  };

  const handleFetchReports = async () => {
    if (!selectedProduceForReports || !reportsDateRange?.[0] || !reportsDateRange?.[1]) {
      message.warning("Пожалуйста, выберите период для отчета.");
      return;
    }

    setReportsLoading(true);
    try {
      const startDate = dayjs(reportsDateRange[0]).format("DD.MM.YY");
      const endDate = dayjs(reportsDateRange[1]).format("DD.MM.YY");
      
      const response = await api.get(`/produce/${selectedProduceForReports._id}/reports`, {
        params: {
          startDate,
          endDate,
        },
      });
      setReportsData(response.data);
      
      // Анализируем тренды
      const allReports = response.data.monthlyReports.flatMap((month: any) => month.reports);
      const trends = analyzeTrends(allReports);
      setTrendAnalysis(trends);
      setPredictionData(trends?.predictions || null);

      // Рассчитываем расходники для общего количества из отчета
      if (selectedProduceForReports && response.data.periodSummary.totalQuantity > 0) {
        const totalProduced = response.data.periodSummary.totalQuantity;
        setTargetQuantityForIngredients(totalProduced);
        const ingredients = calculateIngredients(totalProduced, selectedProduceForReports);
        setIngredientsData(ingredients);
      }
    } catch (error) {
      console.error("Error fetching reports:", error);
      message.error("Ошибка при получении отчетов.");
    } finally {
      setReportsLoading(false);
    }
  };

  // Функция для расчета расходников для заданного количества
  const calculateIngredients = (targetQuantity: number, produce: any) => {
    if (!produce || !targetQuantity) return null;
    
    const materials = produce.materials?.map((material: any) => ({
      ...material,
      requiredQuantity: (material.quantity * targetQuantity).toFixed(3)
    })) || [];
    
    const produces = produce.produces?.map((produceItem: any) => ({
      ...produceItem,
      requiredQuantity: (produceItem.quantity * targetQuantity).toFixed(3)
    })) || [];
    
    return {
      materials,
      produces,
      totalMaterialsCount: materials.length,
      totalProducesCount: produces.length
    };
  };

  // Функция для анализа трендов производства
  const analyzeTrends = (reports: any[]) => {
    if (!reports || reports.length < 3) return null;
    
    // Группируем отчеты по дням
    const dailyData = reports.reduce((acc: any, report: any) => {
      const date = report.date.split(' ')[0];
      if (!acc[date]) {
        acc[date] = { quantity: 0, count: 0, dates: [] };
      }
      acc[date].quantity += report.quantity;
      acc[date].count += 1;
      acc[date].dates.push(report.date);
      return acc;
    }, {});
    
    // Сортируем по датам
    const sortedDates = Object.keys(dailyData).sort();
    const quantities = sortedDates.map(date => dailyData[date].quantity);
    
    // Вычисляем тренд (простая линейная регрессия)
    const n = quantities.length;
    const sumX = (n * (n - 1)) / 2;
    const sumY = quantities.reduce((a: number, b: number) => a + b, 0);
    const sumXY = quantities.reduce((sum: number, y: number, i: number) => sum + (i * y), 0);
    const sumX2 = quantities.reduce((sum: number, y: number, i: number) => sum + (i * i), 0);
    
    const slope = (n * sumXY - sumX * sumY) / (n * sumX2 - sumX * sumX);
    const intercept = (sumY - slope * sumX) / n;
    
    // Вычисляем R² (коэффициент детерминации)
    const meanY = sumY / n;
    const ssRes = quantities.reduce((sum: number, y: number, i: number) => {
      const predicted = slope * i + intercept;
      return sum + Math.pow(y - predicted, 2);
    }, 0);
    const ssTot = quantities.reduce((sum: number, y: number) => sum + Math.pow(y - meanY, 2), 0);
    const rSquared = 1 - (ssRes / ssTot);
    
    // Прогноз на следующие 7 дней
    const predictions = [];
    for (let i = 0; i < 7; i++) {
      const dayIndex = n + i;
      const predicted = slope * dayIndex + intercept;
      predictions.push({
        day: dayIndex + 1,
        predicted: Math.max(0, predicted),
        date: dayjs().add(i + 1, 'day').format('DD.MM.YYYY')
      });
    }
    
    // Анализ сезонности
    const weeklyPattern = [];
    for (let day = 0; day < 7; day++) {
      const dayReports = reports.filter((report: any) => {
        const reportDate = dayjs(report.date, 'DD.MM.YYYY HH:mm');
        return reportDate.day() === day;
      });
      const avgQuantity = dayReports.length > 0 
        ? dayReports.reduce((sum: number, r: any) => sum + r.quantity, 0) / dayReports.length 
        : 0;
      weeklyPattern.push({
        day: day,
        dayName: ['Воскресенье', 'Понедельник', 'Вторник', 'Среда', 'Четверг', 'Пятница', 'Суббота'][day],
        avgQuantity
      });
    }
    
    return {
      slope,
      intercept,
      rSquared,
      trend: slope > 0 ? 'up' : slope < 0 ? 'down' : 'stable',
      trendStrength: Math.abs(slope),
      predictions,
      weeklyPattern,
      dailyData: sortedDates.map((date, index) => ({
        date,
        quantity: quantities[index],
        count: dailyData[date].count
      }))
    };
  };

  const handleExportReports = () => {
    if (!selectedProduceForReports || !reportsDateRange?.[0] || !reportsDateRange?.[1]) {
      message.warning("Пожалуйста, сначала получите отчет для экспорта.");
      return;
    }

    const startDate = dayjs(reportsDateRange[0]).format("DD.MM.YY");
    const endDate = dayjs(reportsDateRange[1]).format("DD.MM.YY");
    
    downloadFile(
      `/export/reports/all`,
      `produce_reports_${selectedProduceForReports.name}_${startDate}_${endDate}.xlsx`,
      {
        startDate,
        endDate,
        produceId: selectedProduceForReports._id,
      }
    );
  };

  const handlePrintReports = () => {
    if (!reportsData) return;

    const printWindow = window.open('', '_blank');
    if (!printWindow) return;

    let printHtml = `
      <html>
        <head>
          <title>Отчет по продукции: ${reportsData.produce.name}</title>
          <style>
            body { font-family: Arial, sans-serif; font-size: 12px; margin: 20px; }
            table { border-collapse: collapse; width: 100%; margin-bottom: 20px; border: 2px solid #000; }
            th, td { border: 1px solid #000; padding: 8px; text-align: left; }
            th { background-color: #f0f0f0; font-weight: bold; }
            .header { text-align: center; margin-bottom: 20px; border: 3px solid #1890ff; padding: 15px; background-color: #f0f8ff; }
            .month-header { background-color: #e6f3ff; font-weight: bold; border: 2px solid #1890ff; padding: 10px; }
            .summary { margin-top: 20px; font-weight: bold; border: 1px solid #d9d9d9; padding: 10px; background-color: #fafafa; }
            .period-summary { background-color: #f9f9f9; padding: 15px; margin: 20px 0; border: 3px solid #1890ff; border-radius: 8px; }
            .stats-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 10px; margin: 20px 0; }
            .stat-card { background: #fff; border: 2px solid #d9d9d9; padding: 10px; text-align: center; border-radius: 5px; }
            .trend-section { border: 2px solid #722ed1; padding: 15px; margin: 20px 0; border-radius: 8px; background-color: #faf5ff; }
            .prediction-table { border: 2px solid #52c41a; margin: 15px 0; }
            .seasonality-table { border: 2px solid #faad14; margin: 15px 0; }
            .no-print { display: none; }
            @media print {
              body { margin: 10px; }
              .header { page-break-after: avoid; }
              .period-summary { page-break-inside: avoid; }
              .trend-section { page-break-inside: avoid; }
            }
          </style>
        </head>
        <body>
          <div class="header">
            <h2>Отчет по продукции: ${reportsData.produce.name}</h2>
            <p>Период: ${reportsDateRange ? `${dayjs(reportsDateRange[0]).format("DD.MM.YYYY")} - ${dayjs(reportsDateRange[1]).format("DD.MM.YYYY")}` : ''}</p>
            <p>Единица измерения: ${reportsData.produce.unitOfMeasurement}</p>
          </div>

          <div class="period-summary">
            <h3>📊 Итоги за период (${reportsData.periodSummary.periodDays} дней)</h3>
            <div class="stats-grid">
              <div class="stat-card">
                <strong>Общее количество:</strong><br>
                ${formatNumber(reportsData.periodSummary.totalQuantity)} ${reportsData.produce.unitOfMeasurement}
              </div>
              <div class="stat-card">
                <strong>Общее количество (кг):</strong><br>
                ${formatNumber(reportsData.periodSummary.totalQuantityKg)} кг
              </div>
              <div class="stat-card">
                <strong>Общий отход:</strong><br>
                ${formatNumber(reportsData.periodSummary.totalWaste)} ${reportsData.produce.unitOfMeasurement}
              </div>
              <div class="stat-card">
                <strong>Количество отчетов</strong><br>
                ${reportsData.periodSummary.totalReports} шт
              </div>
            </div>
          </div>
    `;

    reportsData.monthlyReports.forEach(monthData => {
      printHtml += `
        <h3 class="month-header">📅 ${monthData.monthName}</h3>
        <table>
          <thead>
            <tr>
              <th>№</th>
              <th>Дата</th>
              <th>Склад-менеджер</th>
              <th>Количество</th>
              <th>Количество (кг)</th>
              <th>Отход</th>
              <th>Остаток до</th>
              <th>Остаток после</th>
            </tr>
          </thead>
          <tbody>
      `;

      monthData.reports.forEach((report, index) => {
        printHtml += `
          <tr>
            <td>${index + 1}</td>
            <td>${report.date}</td>
            <td>${report.warehouseMgrName}</td>
            <td>${formatNumber(report.quantity)}</td>
            <td>${formatNumber(report.quantityKg)}</td>
            <td>${formatNumber(report.wasteProduced)}</td>
            <td>${formatNumber(report.residueBefore)}</td>
            <td>${formatNumber(report.residueAfter)}</td>
          </tr>
        `;
      });

      printHtml += `
          </tbody>
        </table>
        <div class="summary">
          <p>Итого за ${monthData.monthName}:</p>
          <p>Количество: ${formatNumber(monthData.totalQuantity)} ${reportsData.produce.unitOfMeasurement}</p>
          <p>Количество (кг): ${formatNumber(monthData.totalQuantityKg)} кг</p>
          <p>Отход: ${formatNumber(monthData.totalWaste)} ${reportsData.produce.unitOfMeasurement}</p>
          <p>Количество отчетов: ${monthData.reportCount}</p>
          <p>Среднее: ${formatNumber(monthData.averageQuantity)} ${reportsData.produce.unitOfMeasurement}</p>
          <p>Максимум: ${formatNumber(monthData.maxQuantity)} ${reportsData.produce.unitOfMeasurement}</p>
          <p>Минимум: ${formatNumber(monthData.minQuantity)} ${reportsData.produce.unitOfMeasurement}</p>
        </div>
      `;
    });

    // Добавляем аналитику трендов в печать
    if (trendAnalysis) {
      printHtml += `
        <div class="trend-section">
          <h3>🚀 Аналитика трендов производства</h3>
          <div class="stats-grid">
            <div class="stat-card">
              <strong>Направление тренда:</strong><br>
              ${trendAnalysis.trend === 'up' ? '📈 Рост' : trendAnalysis.trend === 'down' ? '📉 Спад' : '➡️ Стабильно'}
            </div>
            <div class="stat-card">
              <strong>Сила тренда:</strong><br>
              ${formatNumber(trendAnalysis.trendStrength)} ${reportsData.produce.unitOfMeasurement}/день
            </div>
            <div class="stat-card">
              <strong>Точность прогноза (R²):</strong><br>
              ${formatNumber(trendAnalysis.rSquared * 100)}%
            </div>
          </div>
          
          <h4>🔮 Прогноз на следующие 7 дней:</h4>
          <table class="prediction-table">
            <thead>
              <tr>
                <th>День</th>
                <th>Дата</th>
                <th>Прогноз</th>
              </tr>
            </thead>
            <tbody>
      `;
      
      trendAnalysis.predictions.forEach((prediction: any) => {
        printHtml += `
          <tr>
            <td>${prediction.day}</td>
            <td>${prediction.date}</td>
            <td>${formatNumber(prediction.predicted)} ${reportsData.produce.unitOfMeasurement}</td>
          </tr>
        `;
      });
      
      printHtml += `
            </tbody>
          </table>
          
          <h4>📅 Сезонность по дням недели:</h4>
          <table class="seasonality-table">
            <thead>
              <tr>
                <th>День недели</th>
                <th>Среднее производство</th>
              </tr>
            </thead>
            <tbody>
      `;
      
      trendAnalysis.weeklyPattern.forEach((day: any) => {
        printHtml += `
          <tr>
            <td>${day.dayName}</td>
            <td>${formatNumber(day.avgQuantity)} ${reportsData.produce.unitOfMeasurement}</td>
          </tr>
        `;
      });
      
      printHtml += `
            </tbody>
          </table>
        </div>
      `;
    }

    printHtml += `
        </body>
      </html>
    `;

    printWindow.document.write(printHtml);
    printWindow.document.close();
    printWindow.focus();
    printWindow.print();
  };

  const handleFetchSalesReport = async () => {
    if (!selectedProduceForSalesReport || !salesReportDateRange?.[0] || !salesReportDateRange?.[1] || !salesReportCurrency) {
      message.warning("Пожалуйста, выберите период и валюту для отчета по продажам.");
      return;
    }

    setSalesReportLoading(true);
    try {
      const startDate = dayjs(salesReportDateRange[0]).format("DD.MM.YY");
      const endDate = dayjs(salesReportDateRange[1]).format("DD.MM.YY");
      
      const response = await api.get(`/produce/${selectedProduceForSalesReport._id}/sales-report`, {
        params: {
          startDate,
          endDate,
          currency: salesReportCurrency,
        },
      });
      setSalesReportData(response.data);
    } catch (error) {
      console.error("Error fetching sales report:", error);
      message.error("Ошибка при получении отчета по продажам.");
    } finally {
      setSalesReportLoading(false);
    }
  };

  const handleExportSalesReport = () => {
    if (!selectedProduceForSalesReport || !salesReportDateRange?.[0] || !salesReportDateRange?.[1]) {
      message.warning("Пожалуйста, сначала получите отчет по продажам для экспорта.");
      return;
    }

    const startDate = dayjs(salesReportDateRange[0]).format("DD.MM.YY");
    const endDate = dayjs(salesReportDateRange[1]).format("DD.MM.YY");
    
    downloadFile(
      `/export/reports/all`,
      `produce_sales_reports_${selectedProduceForSalesReport.name}_${startDate}_${endDate}.xlsx`,
      {
        startDate,
        endDate,
        produceId: selectedProduceForSalesReport._id,
      }
    );
  };

  const handlePrintSalesReport = () => {
    if (!salesReportData) return;

    const printWindow = window.open('', '_blank');
    if (!printWindow) return;

    let printHtml = `
      <html>
        <head>
          <title>Отчет по продажам: ${salesReportData.produce.name}</title>
          <style>
            body { font-family: Arial, sans-serif; font-size: 12px; margin: 20px; }
            table { border-collapse: collapse; width: 100%; margin-bottom: 20px; border: 2px solid #000; }
            th, td { border: 1px solid #000; padding: 8px; text-align: left; }
            th { background-color: #f0f0f0; font-weight: bold; }
            .header { text-align: center; margin-bottom: 20px; border: 3px solid #1890ff; padding: 15px; background-color: #f0f8ff; }
            .month-header { background-color: #e6f3ff; font-weight: bold; border: 2px solid #1890ff; padding: 10px; }
            .summary { margin-top: 20px; font-weight: bold; border: 1px solid #d9d9d9; padding: 10px; background-color: #fafafa; }
            .period-summary { background-color: #f9f9f9; padding: 15px; margin: 20px 0; border: 3px solid #1890ff; border-radius: 8px; }
            .stats-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 10px; margin: 20px 0; }
            .stat-card { background: #fff; border: 2px solid #d9d9d9; padding: 10px; text-align: center; border-radius: 5px; }
            .sales-section { border: 2px solid #52c41a; padding: 15px; margin: 20px 0; border-radius: 8px; background-color: #f6ffed; }
            .clients-table { border: 2px solid #faad14; margin: 15px 0; }
            .top-sales-table { border: 2px solid #722ed1; margin: 15px 0; }
            .no-print { display: none; }
            @media print {
              body { margin: 10px; }
              .header { page-break-after: avoid; }
              .period-summary { page-break-inside: avoid; }
              .sales-section { page-break-inside: avoid; }
            }
          </style>
        </head>
        <body>
          <div class="header">
            <h2>Отчет по продажам: ${salesReportData.produce.name}</h2>
            <p>Период: ${salesReportDateRange ? `${dayjs(salesReportDateRange[0]).format("DD.MM.YYYY")} - ${dayjs(salesReportDateRange[1]).format("DD.MM.YYYY")}` : ''}</p>
            <p>Валюта: ${salesReportData.currency}</p>
            <p>Единица измерения: ${salesReportData.produce.unitOfMeasurement}</p>
          </div>

          <div class="period-summary">
            <h3>💰 Итоги продаж за период (${salesReportData.periodSummary.periodDays} дней)</h3>
            <div class="stats-grid">
              <div class="stat-card">
                <strong>Общий объем продаж:</strong><br>
                ${formatNumber(salesReportData.periodSummary.totalSales)} ${salesReportData.currency}
              </div>
              <div class="stat-card">
                <strong>Общий профит:</strong><br>
                ${formatNumber(salesReportData.periodSummary.totalProfit)} ${salesReportData.currency}
              </div>
              <div class="stat-card">
                <strong>Маржа прибыли:</strong><br>
                ${formatNumber(salesReportData.periodSummary.profitMargin)} %
              </div>
              <div class="stat-card">
                <strong>Количество продано:</strong><br>
                ${formatNumber(salesReportData.periodSummary.totalQuantity)} ${salesReportData.produce.unitOfMeasurement}
              </div>
              <div class="stat-card">
                <strong>Количество продано (кг):</strong><br>
                ${formatNumber(salesReportData.periodSummary.totalQuantityKg)} кг
              </div>
              <div class="stat-card">
                <strong>Количество продаж:</strong><br>
                ${salesReportData.periodSummary.salesCount} шт
              </div>
              <div class="stat-card">
                <strong>Средняя цена:</strong><br>
                ${formatNumber(salesReportData.periodSummary.averagePrice)} ${salesReportData.currency}
              </div>
              <div class="stat-card">
                <strong>Средняя себестоимость:</strong><br>
                ${formatNumber(salesReportData.periodSummary.averageCostPrice)} ${salesReportData.currency}
              </div>
              <div class="stat-card">
                <strong>Среднее за продажу:</strong><br>
                ${formatNumber(salesReportData.periodSummary.averageQuantityPerSale)} ${salesReportData.produce.unitOfMeasurement}
              </div>
            </div>
          </div>
    `;

    // Добавляем месячную статистику
    salesReportData.monthlySales.forEach(monthData => {
      printHtml += `
        <h3 class="month-header">📅 ${monthData.monthName}</h3>
        <div class="summary">
          <p>Объем продаж: ${formatNumber(monthData.totalSales)} ${salesReportData.currency}</p>
          <p>Профит: ${formatNumber(monthData.totalProfit)} ${salesReportData.currency}</p>
          <p>Количество: ${formatNumber(monthData.totalQuantity)} ${salesReportData.produce.unitOfMeasurement}</p>
          <p>Количество (кг): ${formatNumber(monthData.totalQuantityKg)} кг</p>
          <p>Количество продаж: ${monthData.salesCount}</p>
          <p>Средняя цена: ${formatNumber(monthData.averagePrice)} ${salesReportData.currency}</p>
          <p>Маржа прибыли: ${formatNumber(monthData.profitMargin)}%</p>
        </div>
      `;
    });

    // Добавляем статистику по клиентам
    if (salesReportData.clientStats.length > 0) {
      printHtml += `
        <h3>👥 Топ-клиенты</h3>
        <table class="clients-table">
          <thead>
            <tr>
              <th>Клиент</th>
              <th>Объем продаж</th>
              <th>Профит</th>
              <th>Количество</th>
              <th>Количество продаж</th>
              <th>Средний чек</th>
              <th>Последний заказ</th>
            </tr>
          </thead>
          <tbody>
      `;
      
      salesReportData.clientStats.slice(0, 10).forEach((client) => {
        printHtml += `
          <tr>
            <td>${client.clientName}</td>
            <td>${formatNumber(client.totalSales)} ${salesReportData.currency}</td>
            <td>${formatNumber(client.totalProfit)} ${salesReportData.currency}</td>
            <td>${formatNumber(client.totalQuantity)} ${salesReportData.produce.unitOfMeasurement}</td>
            <td>${client.salesCount}</td>
            <td>${formatNumber(client.averageOrderValue)} ${salesReportData.currency}</td>
            <td>${client.lastOrderDate}</td>
          </tr>
        `;
      });
      
      printHtml += `
          </tbody>
        </table>
      `;
    }

    // Добавляем топ-продажи
    if (salesReportData.topSales.length > 0) {
      printHtml += `
        <h3>🏆 Топ-продажи</h3>
        <table class="top-sales-table">
          <thead>
            <tr>
              <th>Чек</th>
              <th>Клиент</th>
              <th>Дата</th>
              <th>Количество</th>
              <th>Сумма</th>
              <th>Профит</th>
              <th>Цена</th>
              <th>Скидка</th>
            </tr>
          </thead>
          <tbody>
      `;
      
      salesReportData.topSales.forEach((sale) => {
        printHtml += `
          <tr>
            <td>${sale.receipt}</td>
            <td>${sale.clientName}</td>
            <td>${sale.date}</td>
            <td>${formatNumber(sale.quantity)} ${salesReportData.produce.unitOfMeasurement}</td>
            <td>${formatNumber(sale.totalSales)} ${salesReportData.currency}</td>
            <td>${formatNumber(sale.profit)} ${salesReportData.currency}</td>
            <td>${formatNumber(sale.price)} ${salesReportData.currency}</td>
            <td>${sale.discount}%</td>
          </tr>
        `;
      });
      
      printHtml += `
          </tbody>
        </table>
      `;
    }

    printHtml += `
        </body>
      </html>
    `;

    printWindow.document.write(printHtml);
    printWindow.document.close();
    printWindow.focus();
    printWindow.print();
  };

  const handlePrintProduces = () => {
    if (selectedProduces.length === 0) {
      message.warning("Пожалуйста, выберите хотя бы одну продукцию для печати.");
      return;
    }

    const selectedProducesData = produces.filter(produce => 
      selectedProduces.includes(produce._id)
    );

    const printWindow = window.open('', '_blank');
    if (!printWindow) return;

    const currentDate = new Date().toLocaleDateString('ru-RU');
    
    // Подсчет итогов
    const totalResidue = selectedProducesData.reduce((sum, produce) => sum + (produce.residue || 0), 0);
    const totalResidueKg = selectedProducesData.reduce((sum, produce) => sum + (produce.residueKg || 0), 0);

    const printHtml = `
      <html>
        <head>
          <title>Отчет по продукции</title>
          <style>
            body {
              font-family: Arial, sans-serif;
              font-size: 12px;
              margin: 20px;
              color: #222;
            }
            .header {
              text-align: center;
              margin-bottom: 20px;
              border-bottom: 2px solid #333;
              padding-bottom: 10px;
            }
            .header h1 {
              margin: 0;
              font-size: 18px;
              font-weight: bold;
            }
            .header .info {
              margin-top: 5px;
              font-size: 12px;
              color: #666;
            }
            table {
              width: 100%;
              border-collapse: collapse;
              margin-bottom: 20px;
              border: 2px solid #000;
            }
            th, td {
              border: 1px solid #000;
              padding: 8px;
              text-align: left;
              font-size: 11px;
            }
            th {
              background-color: #f0f0f0;
              font-weight: bold;
              text-align: center;
            }
            tr:nth-child(even) {
              background-color: #f9f9f9;
            }
            .summary {
              margin-top: 20px;
              padding: 15px;
              border: 2px solid #1890ff;
              border-radius: 8px;
              background-color: #f0f8ff;
            }
            .summary h3 {
              margin: 0 0 10px 0;
              font-size: 14px;
              color: #1890ff;
            }
            .summary-grid {
              display: grid;
              grid-template-columns: repeat(2, 1fr);
              gap: 10px;
            }
            .summary-item {
              background: white;
              padding: 8px;
              border: 1px solid #d9d9d9;
              border-radius: 4px;
              text-align: center;
            }
            .summary-item strong {
              display: block;
              margin-bottom: 4px;
              color: #333;
            }
            .low-residue {
              background-color: #fff1f0 !important;
              color: #ff4d4f !important;
            }
            .normal-residue {
              background-color: #fff7e6 !important;
              color: #faad14 !important;
            }
            .high-residue {
              background-color: #f6ffed !important;
              color: #52c41a !important;
            }
            @media print {
              body { margin: 10px; }
              .header { page-break-after: avoid; }
              .summary { page-break-inside: avoid; }
            }
          </style>
        </head>
        <body>
          <div class="header">
            <h1>ОТЧЕТ ПО ПРОДУКЦИИ</h1>
            <div class="info">
              Дата: ${currentDate}<br>
              Количество продукции: ${selectedProducesData.length}
            </div>
          </div>

          <table>
            <thead>
              <tr>
                <th>№</th>
                <th>Название</th>
                <th>Ед. измерения</th>
                <th>Остаток</th>
                <th>Остаток (кг)</th>
                <th>Средняя себестоимость USD</th>
                <th>Средняя себестоимость TJS</th>
                <th>Статус</th>
              </tr>
            </thead>
            <tbody>
              ${selectedProducesData.map((produce, index) => {
                let statusClass = '';
                let statusText = '';
                
                if (produce.lowerLimit !== undefined && produce.upperLimit !== undefined) {
                  if (produce.residue < produce.lowerLimit) {
                    statusClass = 'low-residue';
                    statusText = 'Низкий остаток';
                  } else if (produce.residue <= produce.upperLimit) {
                    statusClass = 'normal-residue';
                    statusText = 'Нормальный остаток';
                  } else {
                    statusClass = 'high-residue';
                    statusText = 'Высокий остаток';
                  }
                }

                return `
                  <tr>
                    <td>${index + 1}</td>
                    <td>${produce.name}</td>
                    <td>${produce.unitOfMeasurement}</td>
                    <td>${formatNumber(produce.residue)}</td>
                    <td>${formatNumber(produce.residueKg)}</td>
                    <td>${produce.averageCost?.averageCostUSD ? formatNumber(produce.averageCost.averageCostUSD) : 'Н/Д'}</td>
                    <td>${produce.averageCost?.averageCostTJS ? formatNumber(produce.averageCost.averageCostTJS) : 'Н/Д'}</td>
                    <td class="${statusClass}">${statusText}</td>
                  </tr>
                `;
              }).join('')}
            </tbody>
          </table>

          <div class="summary">
            <h3>📊 Суммарно</h3>
            <div class="summary-grid">
              <div class="summary-item">
                <strong>Общий остаток:</strong>
                <div style="font-size: 16px; color: #1890ff; font-weight: bold; margin-top: 5px;">
                  ${formatNumber(totalResidue)}
                </div>
              </div>
              <div class="summary-item">
                <strong>Общий остаток (кг):</strong>
                <div style="font-size: 16px; color: #52c41a; font-weight: bold; margin-top: 5px;">
                  ${formatNumber(totalResidueKg)} кг
                </div>
              </div>
            </div>
          </div>
        </body>
      </html>
    `;

    printWindow.document.write(printHtml);
    printWindow.document.close();
    printWindow.focus();
    printWindow.print();
  };

  // Подсчет суммарных остатков
  const totalResidue = produces.reduce((sum, produce) => sum + (produce.residue || 0), 0);
  const totalResidueKg = produces.reduce((sum, produce) => sum + (produce.residueKg || 0), 0);


  const columns = [
    {
      title: "Выбор",
      key: "select",
      width: 80,
      fixed: "left" as "left",
      render: (_: any, record: ResidueType) => (
        <Checkbox
          checked={selectedProduces.includes(record._id)}
          onChange={(e) => handleSelectProduce(record._id, e.target.checked)}
        />
      ),
    },
    {
      title: "Название",
      dataIndex: "name",
      key: "name",
      fixed: "left" as "left",
    },
    {
      title: "Ед. изм.",
      dataIndex: "unitOfMeasurement",
      key: "unitOfMeasurement",
    },
    {
      title: "Статус",
      key: "status",
      width: 80,
      render: (_: any, record: ResidueType) => (
        record.isHidden ? (
          <Tag color="red">Скрыт</Tag>
        ) : (
          <Tag color="green">Активен</Tag>
        )
      ),
    },
    {
      title: "Остаток",
      dataIndex: "residue",
      key: "residue",
      render: (_: any, record: ResidueType) => {
        const { residue, lowerLimit, upperLimit } = record;
        let backgroundColor = "white";

        if (lowerLimit !== undefined && upperLimit !== undefined) {
          if (residue < lowerLimit) {
            backgroundColor = "red";
          } else if (residue <= upperLimit) {
            backgroundColor = "yellow";
          } else if (residue > upperLimit) {
            backgroundColor = "red";
          }
        }

        return {
          props: {
            style: { background: backgroundColor },
          },
          children: <div>{formatNumber(residue)}</div>,
        };
      },
    },
    {
      title: "Остаток км",
      dataIndex: "residueKg",
      key: "residueKg",
      render: (residueKg: number) => formatNumber(residueKg),
    },
    {
      title: "Общий ед.",
      key: "totalQuantity",
      render: (_: any, record: any) => {
        const totalProducesQuantity = record.produces?.reduce(
          (sum: number, item: any) => sum + item.quantity,
          0
        ) || 0;
        const totalMaterialsQuantity = record.materials?.reduce(
          (sum: number, item: any) => sum + item.quantity,
          0
        ) || 0;
        const totalQuantity = totalProducesQuantity + totalMaterialsQuantity;

        return `${totalQuantity} `;
      },
    },

    {
      title: "Материалы",
      key: "materials",
      render: (_: any, record: any) => (
        <ul>
          {record.materials?.map((material: any) => (
            <li key={material.materialId}>
              {material.materialName} ({material.quantity})
            </li>
          )) || []}
        </ul>
      ),
    },
    {
      title: "Продукции",
      key: "produces",
      render: (_: any, record: any) => (
        <ul>
          {record.produces?.map((produce: any) => (
            <li key={produce.produceId}>
              {produce.produceName} ({produce.quantity})
            </li>
          )) || []}
        </ul>
      ),
    },
    ...(authState.user?.role !== UserRole.Foreman
      ? [
          {
            title: "Средняя себестоимость USD",
            key: "averageCostUSD",
            render: (_: any, record: ResidueType) => {
              return record.averageCost
                ? `${formatNumber(record.averageCost.averageCostUSD)}`
                : "Н/Д";
            },
          },
          {
            title: "Средняя себестоимость TJS",
            key: "averageCostTJS",
            render: (_: any, record: ResidueType) => {
              return record.averageCost
                ? `${formatNumber(record.averageCost.averageCostTJS)}`
                : "Н/Д";
            },
          },
        ]
      : []),
    {
      title: "",
      key: "actions",
      render: (_: any, produce: ResidueType) => {
        const menu = (
          <Menu>
            {canEditProduces(authState.user?.role) && (
              <Menu.Item onClick={() => handleEditProduce(produce)}>
                Изменить
              </Menu.Item>
            )}

            <Menu.Item onClick={() => handleOpenQuantityKgModal(produce)}>
              Изменить количество км
            </Menu.Item>

            <Menu.Item
              onClick={() =>
                navigate(`/${produce._id}/produce/${produce.name}/batches`)
              }
            >
              Смотреть партии
            </Menu.Item>

            <Menu.Item
              onClick={() =>
                navigate(
                  `/${produce._id}/produce/${produce.name}/changelogs`
                )
              }
            >
              Смотреть журнал
            </Menu.Item>

            <Menu.Item onClick={() => handleOpenReportsModal(produce)}>
              Отчет по производству
            </Menu.Item>

            <Menu.Item onClick={() => handleOpenSalesReportModal(produce)}>
              Отчет по продажам
            </Menu.Item>

            {authState.user?.role === UserRole.Cashier && (
              <Menu.Item
                onClick={() =>
                  navigate(`/${produce._id}/kind/all/${produce.name}/orders`)
                }
              >
                Смотреть заказы
              </Menu.Item>
            )}
          </Menu>
        );

        return (
          <Dropdown overlay={menu}>
            <Button type="link">
              Действия <DownOutlined />
            </Button>
          </Dropdown>
        );
      },
    },
  ];

  // Функции для изменения количества кг
  const handleOpenQuantityKgModal = (produce: ResidueType) => {
    setSelectedProduceForQuantityKg(produce);
    quantityKgForm.setFieldsValue({
      quantityKg: produce.residueKg,
      reason: "",
    });
    setIsQuantityKgModalOpen(true);
  };

  const handleCloseQuantityKgModal = () => {
    setIsQuantityKgModalOpen(false);
    setSelectedProduceForQuantityKg(null);
    quantityKgForm.resetFields();
  };

  const handleQuantityKgSubmit = async (values: any) => {
    if (!selectedProduceForQuantityKg) return;

    setQuantityKgLoading(true);
    try {
      const response = await api.put(
        `/produce/${selectedProduceForQuantityKg._id}/quantity-kg`,
        {
          quantityKg: values.quantityKg,
          reason: values.reason,
        }
      );

      message.success("Количество км успешно обновлено");
      handleCloseQuantityKgModal();
      fetchProduces(); // Обновляем список продукции
    } catch (error: any) {
      console.error("Error updating quantity kg:", error);
      message.error(error.response?.data?.error || "Ошибка при обновлении количества км");
    } finally {
      setQuantityKgLoading(false);
    }
  };

  const handleHideProduces = async () => {
    if (selectedProduces.length === 0) {
      message.warning("Выберите продукты для скрытия");
      return;
    }

    setHideLoading(true);
    try {
      await api.post("/produce/hide", {
        produceIds: selectedProduces,
      });

      message.success(`Успешно скрыто ${selectedProduces.length} продуктов`);
      setSelectedProduces([]);
      fetchProduces();
    } catch (error: any) {
      console.error("Error hiding produces:", error);
      message.error(error.response?.data?.error || "Ошибка при скрытии продуктов");
    } finally {
      setHideLoading(false);
    }
  };

  const handleShowProduces = async () => {
    if (selectedProduces.length === 0) {
      message.warning("Выберите продукты для показа");
      return;
    }

    setHideLoading(true);
    try {
      await api.post("/produce/show", {
        produceIds: selectedProduces,
      });

      message.success(`Успешно показано ${selectedProduces.length} продуктов`);
      setSelectedProduces([]);
      fetchProduces();
    } catch (error: any) {
      console.error("Error showing produces:", error);
      message.error(error.response?.data?.error || "Ошибка при показе продуктов");
    } finally {
      setHideLoading(false);
    }
  };

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      setSelectedProduces(produces.map(produce => produce._id));
    } else {
      setSelectedProduces([]);
    }
  };

  const handleSelectProduce = (produceId: string, checked: boolean) => {
    if (checked) {
      setSelectedProduces(prev => [...prev, produceId]);
    } else {
      setSelectedProduces(prev => prev.filter(id => id !== produceId));
    }
  };

  return (
    <div style={{ padding: 24 }}>
      <Title>Продукции</Title>
      
      {/* Статистика */}
      <Row gutter={16} style={{ marginBottom: 16 }}>
        <Col span={4}>
          <Card>
            <Statistic
              title="Общий остаток"
              value={formatNumber(totalResidue)}
              precision={3}
            />
          </Card>
        </Col>
        <Col span={4}>
          <Card>
            <Statistic
              title="Общий остаток (км)"
              value={formatNumber(totalResidueKg)}
              precision={3}
            />
          </Card>
        </Col>
        <Col span={4}>
          <Card>
            <Statistic
              title="Количество продукции"
              value={produces.length}
            />
          </Card>
        </Col>
        <Col span={4}>
          <Card>
            <Statistic
              title="Выбрано"
              value={selectedProduces.length}
            />
          </Card>
        </Col>
        <Col span={4}>
          <Card>
            <Statistic
              title="Скрытых"
              value={produces.filter(p => p.isHidden).length}
              valueStyle={{ color: '#cf1322' }}
            />
          </Card>
        </Col>
        <Col span={4}>
          <Card>
            <Statistic
              title="Активных"
              value={produces.filter(p => !p.isHidden).length}
              valueStyle={{ color: '#52c41a' }}
            />
          </Card>
        </Col>
      </Row>

      <Space style={{ marginBottom: 16 }}>
        <Search
          placeholder="Поиск по названию"
          onSearch={handleSearch}
          allowClear
        />
        <Select
          placeholder="Фильтр по ед. измерения"
          onChange={(value) => setUnitFilter(value)}
          allowClear
          style={{ width: 200 }}
        >
          {Object.values(UnitOfMeasurement).map((unit) => (
            <Option key={unit} value={unit}>
              {unit}
            </Option>
          ))}
        </Select>
        <Select
          placeholder="Сортировать по"
          defaultValue="name"
          onChange={(value) => setSortBy(value)}
          style={{ width: 150 }}
        >
          <Option value="name">Название</Option>
          <Option value="totalProduced">Произведено</Option>
          <Option value="createdAt">Дата</Option>
          <Option value="residue">Остаток</Option>
          <Option value="totalSold">Кол-во продаж</Option>
          <Option value="totalPaidAmount">Кол-во покупок</Option>
        </Select>
        <Select
          defaultValue="asc"
          onChange={(value: "asc" | "desc") => setSortOrder(value)}
          style={{ width: 120 }}
        >
          <Option value="asc">По возрастанию</Option>
          <Option value="desc">По убыванию</Option>
        </Select>
        <Checkbox
          checked={belowLimit}
          onChange={(e) => setBelowLimit(e.target.checked)}
        >
          Ниже лимита
        </Checkbox>
        <Checkbox
          checked={hasResidue}
          onChange={(e) => setHasResidue(e.target.checked)}
        >
          Имееть остаток
        </Checkbox>
        <Checkbox
          checked={showHidden}
          onChange={(e) => setShowHidden(e.target.checked)}
        >
          Показать скрытые
        </Checkbox>
        {canEditProduces(authState.user?.role) && (
          <Button type="primary" onClick={handleAddProduce}>
            Добавить
          </Button>
        )}
        {canEditProduces(authState.user?.role) && selectedProduces.length > 0 && (
          <>
            <Button
              type="default"
              danger
              onClick={handleHideProduces}
              loading={hideLoading}
            >
              Скрыть выбранные ({selectedProduces.length})
            </Button>
            <Button
              type="default"
              onClick={handleShowProduces}
              loading={hideLoading}
            >
              Показать выбранные ({selectedProduces.length})
            </Button>
          </>
        )}
        <Button
          type="default"
          loading={downloadLoading}
          icon={<PiMicrosoftExcelLogoFill />}
          onClick={handleExport}
        >
          Экспорт в Excel
        </Button>
        <Button
          type="default"
          onClick={handlePrintProduces}
          disabled={selectedProduces.length === 0}
        >
          Печать ({selectedProduces.length})
        </Button>
      </Space>
      {loading ? (
        <div style={{ textAlign: "center", padding: "50px 0" }}>
          <Spin size="large" />
        </div>
      ) : (
        <div>
          <div style={{ marginBottom: 16, display: 'flex', alignItems: 'center', gap: 16 }}>
            <Checkbox
              checked={selectedProduces.length === produces.length && produces.length > 0}
              indeterminate={selectedProduces.length > 0 && selectedProduces.length < produces.length}
              onChange={(e) => handleSelectAll(e.target.checked)}
            >
              Выбрать все ({selectedProduces.length}/{produces.length})
            </Checkbox>
            {selectedProduces.length > 0 && (
              <Button
                type="link"
                onClick={() => setSelectedProduces([])}
                style={{ padding: 0 }}
              >
                Очистить выбор
              </Button>
            )}
          </div>
          <Table
            columns={columns}
            dataSource={produces}
            rowKey={(record) => record._id}
            pagination={{
              current: page,
              total,
              pageSize,
              onChange: (page, size) => {
                setPage(page);
                if (size !== pageSize) setPageSize(size!);
              },
              showSizeChanger: true,
            }}
            scroll={{ x: "max-content" }}
          />
        </div>
      )}
      <Modal
        title={selectedProduce ? "Изменить продукицию" : "Добавить продукцию"}
        open={isModalOpen}
        onCancel={() => {
          setIsModalOpen(false);
          setSelectedProduce(null);
          form.resetFields();
        }}
        onOk={() => form.submit()}
        confirmLoading={confirmLoading}
      >
        <Form form={form} onFinish={handleFormSubmit} layout="vertical">
          <Form.Item
            name="name"
            label="Название"
            rules={[{ required: true, message: "Введите название" }]}
          >
            <Input />
          </Form.Item>
          <Form.List name="materials">
            {(fields, { add, remove }) => (
              <>
                {fields.map(({ key, name, fieldKey, ...restField }) => (
                  <Space
                    key={key}
                    style={{ display: "flex", marginBottom: 8 }}
                    align="baseline"
                  >
                    <Form.Item
                      {...restField}
                      name={[name, "materialId"]}
                      fieldKey={[fieldKey as number, "materialId"]}
                      rules={[{ required: true, message: "Выберите материал" }]}
                    >
                      <Select
                        placeholder="Материал"
                        loading={materialLoading}
                        style={{ width: 240 }}
                        showSearch
                        filterOption={(input, option) => {
                          const optionText = option?.label || option?.children;
                          return String(optionText).toLowerCase().includes(input.toLowerCase());
                        }}
                        optionFilterProp="children"
                      >
                        {materialsList?.map((material: Material) => (
                          <Option key={material._id} value={material._id}>
                            {material.name}
                          </Option>
                        ))}
                      </Select>
                    </Form.Item>
                    <Form.Item
                      {...restField}
                      name={[name, "quantity"]}
                      fieldKey={[fieldKey as number, "quantity"]}
                      rules={[
                        {
                          required: true,
                          message: "Введите кол-во",
                        },
                      ]}
                    >
                      <Input placeholder="Кол-во использования" type="number" />
                    </Form.Item>
                    <Button type="link" onClick={() => remove(name)}>
                      Удалить
                    </Button>
                  </Space>
                ))}
                <Button type="dashed" onClick={() => add()} block>
                  Добавить материал
                </Button>
              </>
            )}
          </Form.List>
          <div style={{ marginTop: 20 }}>
            <Form.List name="produces">
              {(fields, { add, remove }) => (
                <>
                  {fields.map(({ key, name, fieldKey, ...restField }) => (
                    <Space
                      key={key}
                      style={{ display: "flex", marginBottom: 8 }}
                      align="baseline"
                    >
                      <Form.Item
                        {...restField}
                        name={[name, "produceId"]}
                        fieldKey={[fieldKey as number, "produceId"]}
                        rules={[
                          { required: true, message: "Выберите продукцию" },
                        ]}
                      >
                        <Select
                          placeholder="Продукция"
                          loading={listLoading}
                          style={{ width: 240 }}
                          showSearch
                          filterOption={(input, option) => {
                            const optionText = option?.label || option?.children;
                            return String(optionText).toLowerCase().includes(input.toLowerCase());
                          }}
                          optionFilterProp="children"
                        >
                          {producesList?.map((produce: Produce) => (
                            <Option key={produce._id} value={produce._id}>
                              {produce.name}
                            </Option>
                          ))}
                        </Select>
                      </Form.Item>
                      <Form.Item
                        {...restField}
                        name={[name, "quantity"]}
                        fieldKey={[fieldKey as number, "quantity"]}
                        rules={[
                          {
                            required: true,
                            message: "Введите кол-во",
                          },
                        ]}
                      >
                        <Input
                          placeholder="Кол-во использования"
                          type="number"
                        />
                      </Form.Item>
                      <Button type="link" onClick={() => remove(name)}>
                        Удалить
                      </Button>
                    </Space>
                  ))}
                  <Button type="dashed" onClick={() => add()} block>
                    Добавить продукцию
                  </Button>
                </>
              )}
            </Form.List>
          </div>
          {!selectedProduce && (
            <>
              <Form.Item
                name="unitOfMeasurement"
                label="Ед. измерения"
                rules={[
                  { required: true, message: "Выберите единицу измерения" },
                ]}
              >
                <Select 
                  placeholder="Выберите единицу измерения"
                  showSearch
                  filterOption={(input, option) => {
                    const optionText = option?.label || option?.children;
                    return String(optionText).toLowerCase().includes(input.toLowerCase());
                  }}
                  optionFilterProp="children"
                >
                  {Object.values(UnitOfMeasurement).map((unit) => (
                    <Option key={unit} value={unit}>
                      {unit}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
              <Form.Item name="wasteName" label="Название отхода">
                <Input />
              </Form.Item>
              <Form.Item name="wasteUnit" label="Ед. измерения отхода">
                <Select 
                  placeholder="Выберите единицу измерения"
                  showSearch
                  filterOption={(input, option) => {
                    const optionText = option?.label || option?.children;
                    return String(optionText).toLowerCase().includes(input.toLowerCase());
                  }}
                  optionFilterProp="children"
                >
                  {Object.values(UnitOfMeasurement).map((unit) => (
                    <Option key={unit} value={unit}>
                      {unit}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </>
          )}

          <Form.Item
            name="lowerLimit"
            label="Нижный лимит"
            rules={[{ required: true, message: "Введите лимит" }]}
          >
            <InputNumber min={0} />
          </Form.Item>
          <Form.Item
            name="upperLimit"
            label="Верхний лимит"
            rules={[{ required: true, message: "Введите лимит" }]}
          >
            <InputNumber min={0} />
          </Form.Item>

          <Form.Item
            name={["price", "currency"]}
            label="Валюта"
            rules={[{ required: true, message: "Выберите валюту" }]}
          >
            <Select 
              placeholder="Выберите валюту"
              showSearch
              filterOption={(input, option) => {
                const optionText = option?.label || option?.children;
                return String(optionText).toLowerCase().includes(input.toLowerCase());
              }}
              optionFilterProp="children"
            >
              {Object.values(Currency).map((currency) => (
                <Option key={currency} value={currency}>
                  {currency}
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            name={["price", "amount"]}
            label="Цена"
            rules={[{ required: true, message: "Введите цену" }]}
          >
            <InputNumber min={0} />
          </Form.Item>
        </Form>
      </Modal>

      {/* Модальное окно для отчетов */}
      <Modal
        title={`Отчет по продукции: ${selectedProduceForReports?.name || ''}`}
        open={isReportsModalOpen}
        onCancel={handleCloseReportsModal}
        footer={null}
        width={1400}
      >
        <div style={{ marginBottom: 16 }}>
          <Space>
            <span>Период:</span>
            <RangePicker
              format="DD.MM.YYYY"
              onChange={(dates) => {
                if (dates && dates[0] && dates[1]) {
                  setReportsDateRange([
                    dates[0].format("YYYY-MM-DD"),
                    dates[1].format("YYYY-MM-DD")
                  ]);
                } else {
                  setReportsDateRange(null);
                }
              }}
            />
            <Button
              type="primary"
              loading={reportsLoading}
              onClick={handleFetchReports}
            >
              Получить отчет
            </Button>
            {reportsData && (
              <>
                <Button onClick={handlePrintReports}>
                  Печать
                </Button>
                <Button
                  type="default"
                  loading={downloadLoading}
                  icon={<PiMicrosoftExcelLogoFill />}
                  onClick={handleExportReports}
                >
                  Экспорт в Excel
                </Button>
              </>
            )}
          </Space>
        </div>

        {reportsLoading ? (
          <div style={{ textAlign: 'center', padding: 32 }}>
            <Spin size="large" />
          </div>
        ) : reportsData ? (
          <Tabs
            defaultActiveKey="summary"
            items={[
              {
                key: 'summary',
                label: 'Сводка производства',
                children: (
                  <div>
                    {/* Итоги за период */}
                    <Card style={{ marginBottom: 16, border: '2px solid #1890ff' }}>
              <Title level={4}>
                <TrophyOutlined style={{ marginRight: 8 }} />
                Итоги за период ({reportsData.periodSummary.periodDays} дней)
                <Tooltip title="Общая статистика производства за выбранный период. Включает все отчеты и их анализ">
                  <InfoCircleOutlined style={{ marginLeft: 8, color: '#1890ff' }} />
                </Tooltip>
              </Title>
              <Row gutter={16}>
                <Col span={6}>
                  <Statistic
                    title="Общее количество"
                    value={formatNumber(reportsData.periodSummary.totalQuantity)}
                    suffix={reportsData.produce.unitOfMeasurement}
                    valueStyle={{ color: '#1890ff' }}
                  />
                </Col>
                <Col span={6}>
                  <Statistic
                    title="Общее количество (кг)"
                    value={formatNumber(reportsData.periodSummary.totalQuantityKg)}
                    suffix="кг"
                    valueStyle={{ color: '#52c41a' }}
                  />
                </Col>
                <Col span={6}>
                  <Statistic
                    title="Общий отход"
                    value={formatNumber(reportsData.periodSummary.totalWaste)}
                    suffix={reportsData.produce.unitOfMeasurement}
                    valueStyle={{ color: '#faad14' }}
                  />
                </Col>
                <Col span={6}>
                  <Statistic
                    title="Количество отчетов"
                    value={reportsData.periodSummary.totalReports}
                    suffix="шт"
                    valueStyle={{ color: '#722ed1' }}
                  />
                </Col>
              </Row>
              <Row gutter={16} style={{ marginTop: 16 }}>
                <Col span={6}>
                  <Statistic
                    title="Среднее за отчет"
                    value={formatNumber(reportsData.periodSummary.averageQuantity)}
                    suffix={reportsData.produce.unitOfMeasurement}
                    valueStyle={{ color: '#13c2c2' }}
                  />
                </Col>
                <Col span={6}>
                  <Statistic
                    title="Максимум"
                    value={formatNumber(reportsData.periodSummary.maxQuantity)}
                    suffix={reportsData.produce.unitOfMeasurement}
                    valueStyle={{ color: '#eb2f96' }}
                  />
                </Col>
                <Col span={6}>
                  <Statistic
                    title="Минимум"
                    value={formatNumber(reportsData.periodSummary.minQuantity)}
                    suffix={reportsData.produce.unitOfMeasurement}
                    valueStyle={{ color: '#f5222d' }}
                  />
                </Col>
                <Col span={6}>
                  <Statistic
                    title="Среднее (кг)"
                    value={formatNumber(reportsData.periodSummary.averageQuantityKg)}
                    suffix="кг"
                    valueStyle={{ color: '#fa8c16' }}
                  />
                </Col>
              </Row>
            </Card>

            {/* Статистика по склад-менеджерам */}
            {reportsData.warehouseMgrStats.length > 0 && (
              <Card style={{ marginBottom: 16 }}>
                <Title level={4}>
                  <UserOutlined style={{ marginRight: 8 }} />
                  Статистика по склад-менеджерам
                  <Tooltip title="Сравнение эффективности работы склад-менеджеров. Показывает количество произведенной продукции и среднюю эффективность на отчет">
                    <InfoCircleOutlined style={{ marginLeft: 8, color: '#1890ff' }} />
                  </Tooltip>
                </Title>
                <Table
                  dataSource={reportsData.warehouseMgrStats}
                  pagination={false}
                  size="small"
                  columns={[
                    {
                      title: 'Склад-менеджер',
                      dataIndex: 'name',
                      key: 'name',
                      render: (name: string, record: any, index: number) => (
                        <Space>
                          {index === 0 && <Tag color="gold">🥇</Tag>}
                          {index === 1 && <Tag color="silver">🥈</Tag>}
                          {index === 2 && <Tag color="bronze">🥉</Tag>}
                          <span style={{ fontWeight: 'bold' }}>{name}</span>
                        </Space>
                      )
                    },
                    {
                      title: 'Количество',
                      dataIndex: 'totalQuantity',
                      key: 'totalQuantity',
                      render: (value: number) => formatNumber(value)
                    },
                    {
                      title: 'Количество (кг)',
                      dataIndex: 'totalQuantityKg',
                      key: 'totalQuantityKg',
                      render: (value: number) => formatNumber(value)
                    },
                    {
                      title: 'Отход',
                      dataIndex: 'totalWaste',
                      key: 'totalWaste',
                      render: (value: number) => formatNumber(value)
                    },
                    {
                      title: 'Отчетов',
                      dataIndex: 'reportCount',
                      key: 'reportCount'
                    },
                    {
                      title: (
                        <Tooltip title="Среднее количество продукции на один отчет. Прогресс-бар показывает отношение к общему среднему">
                          <span>Эффективность <InfoCircleOutlined style={{ marginLeft: 4, color: '#1890ff' }} /></span>
                        </Tooltip>
                      ),
                      key: 'efficiency',
                      render: (_: any, record: any) => {
                        const efficiency = record.totalQuantity / record.reportCount;
                        const percentage = (efficiency / reportsData.periodSummary.averageQuantity) * 100;
                        return (
                          <div>
                            <div style={{ fontWeight: 'bold', marginBottom: 4 }}>
                              {formatNumber(efficiency)} {reportsData.produce.unitOfMeasurement}/отчет
                            </div>
                            <Progress 
                              percent={Math.min(percentage, 100)} 
                              size="small" 
                              status={percentage > 100 ? 'success' : 'normal'}
                              format={(percent) => `${formatNumber(percent || 0)}%`}
                            />
                            <div style={{ fontSize: '12px', color: '#666', marginTop: 4 }}>
                              {percentage > 100 ? 'Выше среднего' : percentage > 80 ? 'Близко к среднему' : 'Ниже среднего'}
                            </div>
                          </div>
                        );
                      }
                    }
                  ]}
                />
              </Card>
            )}

            {/* Детальные отчеты по месяцам */}
            {reportsData.monthlyReports.map((monthData) => (
              <Card key={monthData.monthKey} style={{ marginBottom: 16 }}>
                <Title level={5} style={{ backgroundColor: '#f0f0f0', padding: '8px', marginBottom: '8px' }}>
                  <BarChartOutlined style={{ marginRight: 8 }} />
                  {monthData.monthName}
                </Title>
                
                <Table
                  dataSource={monthData.reports}
                  pagination={false}
                  size="small"
                  columns={[
                    {
                      title: '№',
                      key: 'index',
                      width: 60,
                      render: (_: any, __: any, index: number) => index + 1
                    },
                    {
                      title: 'Дата',
                      dataIndex: 'date',
                      key: 'date',
                      width: 120
                    },
                    {
                      title: 'Склад-менеджер',
                      dataIndex: 'warehouseMgrName',
                      key: 'warehouseMgrName',
                      width: 150
                    },
                    {
                      title: 'Количество',
                      dataIndex: 'quantity',
                      key: 'quantity',
                      width: 100,
                      render: (value: number) => formatNumber(value)
                    },
                    {
                      title: 'Количество (кг)',
                      dataIndex: 'quantityKg',
                      key: 'quantityKg',
                      width: 120,
                      render: (value: number) => formatNumber(value)
                    },
                    {
                      title: 'Отход',
                      dataIndex: 'wasteProduced',
                      key: 'wasteProduced',
                      width: 100,
                      render: (value: number) => formatNumber(value)
                    },
                    {
                      title: 'Остаток до',
                      dataIndex: 'residueBefore',
                      key: 'residueBefore',
                      width: 100,
                      render: (value: number) => formatNumber(value)
                    },
                    {
                      title: 'Остаток после',
                      dataIndex: 'residueAfter',
                      key: 'residueAfter',
                      width: 100,
                      render: (value: number) => formatNumber(value)
                    }
                  ]}
                  scroll={{ x: 800 }}
                />

                <div style={{ marginTop: 8, padding: '8px', backgroundColor: '#f9f9f9' }}>
                  <Row gutter={16}>
                                         <Col span={6}>
                       <Statistic
                         title="Итого количество"
                         value={formatNumber(monthData.totalQuantity)}
                         suffix={reportsData.produce.unitOfMeasurement}
                       />
                     </Col>
                     <Col span={6}>
                       <Statistic
                         title="Итого (кг)"
                         value={formatNumber(monthData.totalQuantityKg)}
                         suffix="кг"
                       />
                     </Col>
                     <Col span={6}>
                       <Statistic
                         title="Итого отход"
                         value={formatNumber(monthData.totalWaste)}
                         suffix={reportsData.produce.unitOfMeasurement}
                       />
                     </Col>
                     <Col span={6}>
                       <Statistic
                         title="Отчетов"
                         value={monthData.reportCount}
                         suffix="шт"
                       />
                     </Col>
                   </Row>
                   <Row gutter={16} style={{ marginTop: 8 }}>
                     <Col span={8}>
                       <Statistic
                         title="Среднее"
                         value={formatNumber(monthData.averageQuantity)}
                         suffix={reportsData.produce.unitOfMeasurement}
                       />
                     </Col>
                     <Col span={8}>
                       <Statistic
                         title="Максимум"
                         value={formatNumber(monthData.maxQuantity)}
                         suffix={reportsData.produce.unitOfMeasurement}
                       />
                     </Col>
                     <Col span={8}>
                       <Statistic
                         title="Минимум"
                         value={formatNumber(monthData.minQuantity)}
                         suffix={reportsData.produce.unitOfMeasurement}
                       />
                     </Col>
                  </Row>
                </div>
              </Card>
            ))}
                  </div>
                ),
              },
              {
                key: 'trends',
                label: (
                  <span>
                    <RocketOutlined style={{ marginRight: 8 }} />
                    Аналитика трендов
                  </span>
                ),
                children: (
                  <div>
                    {trendAnalysis ? (
                      <>
                        {/* Анализ тренда */}
                        <Card style={{ marginBottom: 16 }}>
                          <Title level={4}>
                            <RocketOutlined style={{ marginRight: 8 }} />
                            Анализ тренда производства
                            <Tooltip title="Анализ показывает направление и силу изменения производства во времени. Положительный тренд означает рост производства, отрицательный - спад.">
                              <InfoCircleOutlined style={{ marginLeft: 8, color: '#1890ff' }} />
                            </Tooltip>
                          </Title>
                          
                          <Row gutter={16} style={{ marginBottom: 16 }}>
                            <Col span={8}>
                              <Statistic
                                title={
                                  <Tooltip title="Показывает общее направление изменения производства: рост, спад или стабильность">
                                    <span>Направление тренда <InfoCircleOutlined style={{ marginLeft: 4, color: '#1890ff' }} /></span>
                                  </Tooltip>
                                }
                                value={trendAnalysis.trend === 'up' ? '📈 Рост' : trendAnalysis.trend === 'down' ? '📉 Спад' : '➡️ Стабильно'}
                                valueStyle={{ 
                                  color: trendAnalysis.trend === 'up' ? '#52c41a' : trendAnalysis.trend === 'down' ? '#f5222d' : '#1890ff'
                                }}
                              />
                              <div style={{ marginTop: 8 }}>
                                <Tag color={trendAnalysis.trend === 'up' ? 'green' : trendAnalysis.trend === 'down' ? 'red' : 'blue'}>
                                  {trendAnalysis.trend === 'up' ? 'Тренд роста' : trendAnalysis.trend === 'down' ? 'Тренд спада' : 'Стабильный тренд'}
                                </Tag>
                              </div>
                            </Col>
                            <Col span={8}>
                              <Statistic
                                title={
                                  <Tooltip title="Показывает скорость изменения производства в единицах в день. Чем выше значение, тем быстрее меняется производство">
                                    <span>Сила тренда <InfoCircleOutlined style={{ marginLeft: 4, color: '#1890ff' }} /></span>
                                  </Tooltip>
                                }
                                value={formatNumber(trendAnalysis.trendStrength)}
                                suffix={`${reportsData.produce.unitOfMeasurement}/день`}
                                valueStyle={{ color: '#722ed1' }}
                              />
                              <div style={{ marginTop: 8 }}>
                                <Progress 
                                  percent={Math.min(trendAnalysis.trendStrength * 10, 100)} 
                                  size="small" 
                                  status={trendAnalysis.trendStrength > 5 ? 'success' : 'normal'}
                                  format={(percent) => `${formatNumber(percent || 0)}%`}
                                />
                              </div>
                            </Col>
                            <Col span={8}>
                              <Statistic
                                title={
                                  <Tooltip title="Коэффициент детерминации показывает точность прогноза. Чем ближе к 100%, тем надежнее прогноз. Выше 70% - отлично, 50-70% - хорошо, ниже 50% - требует больше данных">
                                    <span>Точность прогноза (R²) <InfoCircleOutlined style={{ marginLeft: 4, color: '#1890ff' }} /></span>
                                  </Tooltip>
                                }
                                value={formatNumber(trendAnalysis.rSquared * 100)}
                                suffix="%"
                                valueStyle={{ 
                                  color: trendAnalysis.rSquared > 0.7 ? '#52c41a' : trendAnalysis.rSquared > 0.5 ? '#faad14' : '#f5222d'
                                }}
                                precision={2}
                              />
                              <Progress 
                                percent={trendAnalysis.rSquared * 100} 
                                size="small" 
                                status={trendAnalysis.rSquared > 0.7 ? 'success' : trendAnalysis.rSquared > 0.5 ? 'normal' : 'exception'}
                                format={(percent) => `${formatNumber(percent || 0)}%`}
                              />
                            </Col>
                          </Row>

                          {/* Прогноз на 7 дней */}
                          <Card style={{ marginBottom: 16 }}>
                            <Title level={5}>
                              Прогноз на следующие 7 дней
                              <Tooltip title="Прогноз основан на линейной регрессии исторических данных. Показывает ожидаемое производство на каждый день">
                                <InfoCircleOutlined style={{ marginLeft: 8, color: '#1890ff' }} />
                              </Tooltip>
                            </Title>
                            <Table
                              dataSource={trendAnalysis.predictions}
                              pagination={false}
                              size="small"
                              columns={[
                                {
                                  title: 'День',
                                  dataIndex: 'day',
                                  key: 'day',
                                  width: 80
                                },
                                {
                                  title: 'Дата',
                                  dataIndex: 'date',
                                  key: 'date',
                                  width: 120
                                },
                                {
                                  title: 'Прогноз',
                                  dataIndex: 'predicted',
                                  key: 'predicted',
                                  render: (value: number) => (
                                    <span style={{ 
                                      color: value > reportsData.periodSummary.averageQuantity ? '#52c41a' : '#f5222d',
                                      fontWeight: 'bold'
                                    }}>
                                      {formatNumber(value)} {reportsData.produce.unitOfMeasurement}
                                    </span>
                                  )
                                }
                              ]}
                            />
                          </Card>

                          {/* Сезонность по дням недели */}
                          <Card style={{ marginBottom: 16 }}>
                            <Title level={5}>
                              Сезонность по дням недели
                              <Tooltip title="Показывает среднее производство по дням недели. Помогает выявить пиковые и низкие дни для оптимизации графика">
                                <InfoCircleOutlined style={{ marginLeft: 8, color: '#1890ff' }} />
                              </Tooltip>
                            </Title>
                            <Table
                              dataSource={trendAnalysis.weeklyPattern}
                              pagination={false}
                              size="small"
                              columns={[
                                {
                                  title: 'День недели',
                                  dataIndex: 'dayName',
                                  key: 'dayName',
                                  width: 150
                                },
                                {
                                  title: 'Среднее производство',
                                  dataIndex: 'avgQuantity',
                                  key: 'avgQuantity',
                                  render: (value: number) => (
                                    <span style={{ 
                                      color: value > reportsData.periodSummary.averageQuantity ? '#52c41a' : '#f5222d'
                                    }}>
                                      {formatNumber(value)} {reportsData.produce.unitOfMeasurement}
                                    </span>
                                  )
                                }
                              ]}
                            />
                          </Card>

                          {/* Рекомендации */}
                          <Alert
                            message={
                              <span>
                                Рекомендации по производству
                                <Tooltip title="Автоматические рекомендации основаны на анализе трендов и сезонности. Помогают оптимизировать производственные процессы">
                                  <InfoCircleOutlined style={{ marginLeft: 8, color: '#1890ff' }} />
                                </Tooltip>
                              </span>
                            }
                            description={
                              <div>
                                {trendAnalysis.trend === 'up' && (
                                  <p>✅ <strong>Тренд роста!</strong> Рекомендуется увеличить производственные мощности и запасы сырья.</p>
                                )}
                                {trendAnalysis.trend === 'down' && (
                                  <p>⚠️ <strong>Тренд спада!</strong> Рекомендуется проанализировать причины и оптимизировать процессы.</p>
                                )}
                                {trendAnalysis.rSquared < 0.5 && (
                                  <p>📊 <strong>Низкая точность прогноза.</strong> Рекомендуется собрать больше данных для улучшения анализа.</p>
                                )}
                                {trendAnalysis.weeklyPattern.some((day: any) => day.avgQuantity > reportsData.periodSummary.averageQuantity * 1.2) && (
                                  <p>🎯 <strong>Обнаружены пиковые дни!</strong> Рекомендуется оптимизировать график производства.</p>
                                )}
                              </div>
                            }
                            type={trendAnalysis.trend === 'up' ? 'success' : trendAnalysis.trend === 'down' ? 'warning' : 'info'}
                            showIcon
                            style={{ marginTop: 16 }}
                          />
                        </Card>
                      </>
                    ) : (
                      <div style={{ textAlign: 'center', padding: 32, color: '#888' }}>
                        <RocketOutlined style={{ fontSize: 48, marginBottom: 16 }} />
                        <p>Для анализа трендов необходимо минимум 3 отчета</p>
                      </div>
                    )}
                  </div>
                ),
              },
              {
                key: 'ingredients',
                label: (
                  <span>
                    <InfoCircleOutlined style={{ marginRight: 8 }} />
                    Калькулятор расходников
                  </span>
                ),
                children: (
                  <div>
                    <Card style={{ marginBottom: 16 }}>
                      <Title level={4}>
                        <InfoCircleOutlined style={{ marginRight: 8 }} />
                        Расчет расходников для производства
                        <Tooltip title="Показывает какое количество материалов и продукции необходимо для производства заданного количества продукции">
                          <InfoCircleOutlined style={{ marginLeft: 8, color: '#1890ff' }} />
                        </Tooltip>
                      </Title>
                      
                      <div style={{ marginBottom: 16 }}>
                        <Space>
                          <span>Произведенное количество (из отчета):</span>
                          <InputNumber
                            value={targetQuantityForIngredients}
                            onChange={handleTargetQuantityChange}
                            min={0.001}
                            step={1}
                            precision={3}
                            style={{ width: 150 }}
                            addonAfter={selectedProduceForReports?.unitOfMeasurement || ''}
                          />
                          <span style={{ color: '#666', fontSize: '12px' }}>
                            (можно изменить для других расчетов)
                          </span>
                        </Space>
                      </div>

                      {ingredientsData ? (
                        <>
                          <Card style={{ marginBottom: 16 }}>
                            <Title level={5} style={{ marginBottom: 16, color: '#1890ff' }}>
                              Расходники использованные для производства {formatNumber(targetQuantityForIngredients)} {selectedProduceForReports?.unitOfMeasurement}:
                            </Title>
                            
                            <div style={{ fontSize: '16px', lineHeight: '1.8' }}>
                              {/* Материалы */}
                              {ingredientsData.materials.length > 0 && (
                                <div style={{ marginBottom: 20 }}>
                                  <Title level={5} style={{ color: '#52c41a', marginBottom: 12 }}>
                                    📦 Материалы:
                                  </Title>
                                  {ingredientsData.materials.map((material: any, index: number) => (
                                    <div key={material.materialId} style={{ marginBottom: 8, paddingLeft: 16 }}>
                                      <span style={{ fontWeight: 'bold' }}>{material.materialName}</span> - {' '}
                                      <span style={{ color: '#1890ff', fontWeight: 'bold', fontSize: '18px' }}>
                                        {formatNumber(parseFloat(material.requiredQuantity))} {material.unitOfMeasurement}
                                      </span>
                                    </div>
                                  ))}
                                </div>
                              )}

                              {/* Продукция */}
                              {ingredientsData.produces.length > 0 && (
                                <div style={{ marginBottom: 20 }}>
                                  <Title level={5} style={{ color: '#722ed1', marginBottom: 12 }}>
                                    🏭 Продукция:
                                  </Title>
                                  {ingredientsData.produces.map((produce: any, index: number) => (
                                    <div key={produce.produceId} style={{ marginBottom: 8, paddingLeft: 16 }}>
                                      <span style={{ fontWeight: 'bold' }}>{produce.produceName}</span> - {' '}
                                      <span style={{ color: '#722ed1', fontWeight: 'bold', fontSize: '18px' }}>
                                        {formatNumber(parseFloat(produce.requiredQuantity))} {produce.unitOfMeasurement}
                                      </span>
                                    </div>
                                  ))}
                                </div>
                              )}

                              {/* Если нет расходников */}
                              {ingredientsData.materials.length === 0 && ingredientsData.produces.length === 0 && (
                                <Alert
                                  message="Расходники не требуются"
                                  description="Эта продукция не требует дополнительных материалов или продукции для производства (базовое сырье)"
                                  type="info"
                                  showIcon
                                />
                              )}
                            </div>
                          </Card>

                          {/* Краткая итоговая информация */}
                          <Alert
                            message={`Итого использовано для производства ${formatNumber(targetQuantityForIngredients)} ${selectedProduceForReports?.unitOfMeasurement} продукции "${selectedProduceForReports?.name}"`}
                            description={
                              <div style={{ fontSize: '14px' }}>
                                {ingredientsData.totalMaterialsCount > 0 && (
                                  <span>Материалов: <strong>{ingredientsData.totalMaterialsCount}</strong> видов</span>
                                )}
                                {ingredientsData.totalMaterialsCount > 0 && ingredientsData.totalProducesCount > 0 && (
                                  <span> • </span>
                                )}
                                {ingredientsData.totalProducesCount > 0 && (
                                  <span>Продукции: <strong>{ingredientsData.totalProducesCount}</strong> видов</span>
                                )}
                              </div>
                            }
                            type="success"
                            showIcon
                            style={{ marginBottom: 16 }}
                          />
                        </>
                      ) : (
                        <div style={{ textAlign: 'center', padding: 32, color: '#888' }}>
                          <InfoCircleOutlined style={{ fontSize: 48, marginBottom: 16 }} />
                          <p>Сначала получите отчет для расчета расходников</p>
                        </div>
                      )}
                    </Card>
                  </div>
                ),
              }
            ]}
          />
        ) : (
          <div style={{ textAlign: 'center', padding: 32, color: '#888' }}>
            Выберите период и нажмите "Получить отчет"
          </div>
        )}
      </Modal>

      {/* Модальное окно для отчетов по продажам */}
      <Modal
        title={`Отчет по продажам: ${selectedProduceForSalesReport?.name || ''}`}
        open={isSalesReportModalOpen}
        onCancel={handleCloseSalesReportModal}
        footer={null}
        width={1400}
      >
        <div style={{ marginBottom: 16 }}>
          <Space>
            <span>Период:</span>
            <RangePicker
              format="DD.MM.YYYY"
              value={salesReportDateRange ? [dayjs(salesReportDateRange[0]), dayjs(salesReportDateRange[1])] : null}
              onChange={(dates) => {
                if (dates && dates[0] && dates[1]) {
                  setSalesReportDateRange([
                    dates[0].format("YYYY-MM-DD"),
                    dates[1].format("YYYY-MM-DD")
                  ]);
                } else {
                  setSalesReportDateRange(null);
                }
              }}
            />
            <span>Валюта:</span>
            <Select
              value={salesReportCurrency}
              onChange={setSalesReportCurrency}
              style={{ width: 120 }}
            >
              <Option value={Currency.US_DOLLAR}>USD</Option>
              <Option value={Currency.UZBEK_SOM}>TJS</Option>
            </Select>
            <Button
              type="primary"
              loading={salesReportLoading}
              onClick={handleFetchSalesReport}
            >
              Получить отчет
            </Button>
            {salesReportData && (
              <>
                <Button onClick={handlePrintSalesReport}>
                  Печать
                </Button>
                <Button
                  type="default"
                  loading={downloadLoading}
                  icon={<PiMicrosoftExcelLogoFill />}
                  onClick={handleExportSalesReport}
                >
                  Экспорт в Excel
                </Button>
              </>
            )}
          </Space>
        </div>

        {salesReportLoading ? (
          <div style={{ textAlign: 'center', padding: 32 }}>
            <Spin size="large" />
          </div>
        ) : salesReportData ? (
          <div>
            {/* Итоги продаж за период */}
            <Card style={{ marginBottom: 16, border: '2px solid #52c41a' }}>
              <Title level={4}>
                <DollarOutlined style={{ marginRight: 8 }} />
                Итоги продаж за период ({salesReportData.periodSummary.periodDays} дней)
                <Tooltip title="Общая статистика продаж за выбранный период в выбранной валюте. Включает объем продаж, прибыль, количество и аналитику">
                  <InfoCircleOutlined style={{ marginLeft: 8, color: '#1890ff' }} />
                </Tooltip>
              </Title>
              <Row gutter={16}>
                <Col span={6}>
                  <Statistic
                    title="Общий объем продаж"
                    value={formatNumber(salesReportData.periodSummary.totalSales)}
                    suffix={salesReportData.currency}
                    valueStyle={{ color: '#52c41a' }}
                  />
                </Col>
                <Col span={6}>
                  <Statistic
                    title="Общий профит"
                    value={formatNumber(salesReportData.periodSummary.totalProfit)}
                    suffix={salesReportData.currency}
                    valueStyle={{ color: '#1890ff' }}
                  />
                </Col>
                <Col span={6}>
                  <Statistic
                    title="Маржа прибыли"
                    value={formatNumber(salesReportData.periodSummary.profitMargin)}
                    suffix="%"
                    valueStyle={{ color: '#722ed1' }}
                  />
                </Col>
                <Col span={6}>
                  <Statistic
                    title="Количество продаж"
                    value={salesReportData.periodSummary.salesCount}
                    suffix="шт"
                    valueStyle={{ color: '#fa8c16' }}
                  />
                </Col>
              </Row>
              <Row gutter={16} style={{ marginTop: 16 }}>
                <Col span={6}>
                  <Statistic
                    title="Количество продано"
                    value={formatNumber(salesReportData.periodSummary.totalQuantity)}
                    suffix={salesReportData.produce.unitOfMeasurement}
                    valueStyle={{ color: '#13c2c2' }}
                  />
                </Col>
                <Col span={6}>
                  <Statistic
                    title="Количество продано (кг)"
                    value={formatNumber(salesReportData.periodSummary.totalQuantityKg)}
                    suffix="кг"
                    valueStyle={{ color: '#eb2f96' }}
                  />
                </Col>
                <Col span={6}>
                  <Statistic
                    title="Средняя цена"
                    value={formatNumber(salesReportData.periodSummary.averagePrice)}
                    suffix={salesReportData.currency}
                    valueStyle={{ color: '#f5222d' }}
                  />
                </Col>
                <Col span={6}>
                  <Statistic
                    title="Средняя себестоимость"
                    value={formatNumber(salesReportData.periodSummary.averageCostPrice)}
                    suffix={salesReportData.currency}
                    valueStyle={{ color: '#faad14' }}
                  />
                </Col>
              </Row>
              <Row gutter={16} style={{ marginTop: 16 }}>
                <Col span={8}>
                  <Statistic
                    title="Среднее за продажу"
                    value={formatNumber(salesReportData.periodSummary.averageQuantityPerSale)}
                    suffix={salesReportData.produce.unitOfMeasurement}
                    valueStyle={{ color: '#096dd9' }}
                  />
                </Col>
                <Col span={8}>
                  <Statistic
                    title="Среднее в день"
                    value={formatNumber(salesReportData.periodSummary.averageDailySales)}
                    suffix={salesReportData.currency}
                    valueStyle={{ color: '#52c41a' }}
                  />
                </Col>
                <Col span={8}>
                  <Statistic
                    title="Общая себестоимость"
                    value={formatNumber(salesReportData.periodSummary.totalCostOfSales)}
                    suffix={salesReportData.currency}
                    valueStyle={{ color: '#722ed1' }}
                  />
                </Col>
              </Row>
            </Card>

            {/* Топ-клиенты */}
            {salesReportData.clientStats.length > 0 && (
              <Card style={{ marginBottom: 16 }}>
                <Title level={4}>
                  <UserOutlined style={{ marginRight: 8 }} />
                  Топ-клиенты
                  <Tooltip title="Список клиентов, отсортированный по объему продаж. Показывает самых ценных клиентов и их покупательную активность">
                    <InfoCircleOutlined style={{ marginLeft: 8, color: '#1890ff' }} />
                  </Tooltip>
                </Title>
                <Table
                  dataSource={salesReportData.clientStats.slice(0, 10)}
                  pagination={false}
                  size="small"
                  columns={[
                    {
                      title: 'Клиент',
                      dataIndex: 'clientName',
                      key: 'clientName',
                      render: (name: string, record: any, index: number) => (
                        <Space>
                          {index === 0 && <Tag color="gold">🥇</Tag>}
                          {index === 1 && <Tag color="silver">🥈</Tag>}
                          {index === 2 && <Tag color="bronze">🥉</Tag>}
                          <span style={{ fontWeight: 'bold' }}>{name}</span>
                        </Space>
                      )
                    },
                    {
                      title: 'Объем продаж',
                      dataIndex: 'totalSales',
                      key: 'totalSales',
                      render: (value: number) => `${formatNumber(value)} ${salesReportData.currency}`
                    },
                    {
                      title: 'Профит',
                      dataIndex: 'totalProfit',
                      key: 'totalProfit',
                      render: (value: number) => `${formatNumber(value)} ${salesReportData.currency}`
                    },
                    {
                      title: 'Количество',
                      dataIndex: 'totalQuantity',
                      key: 'totalQuantity',
                      render: (value: number) => `${formatNumber(value)} ${salesReportData.produce.unitOfMeasurement}`
                    },
                    {
                      title: 'Количество продаж',
                      dataIndex: 'salesCount',
                      key: 'salesCount'
                    },
                    {
                      title: 'Средний чек',
                      dataIndex: 'averageOrderValue',
                      key: 'averageOrderValue',
                      render: (value: number) => `${formatNumber(value)} ${salesReportData.currency}`
                    },
                    {
                      title: 'Последний заказ',
                      dataIndex: 'lastOrderDate',
                      key: 'lastOrderDate'
                    }
                  ]}
                />
              </Card>
            )}

            {/* Продажи по месяцам */}
            {salesReportData.monthlySales.map((monthData) => (
              <Card key={monthData.monthKey} style={{ marginBottom: 16 }}>
                <Title level={5} style={{ backgroundColor: '#f0f0f0', padding: '8px', marginBottom: '8px' }}>
                  <BarChartOutlined style={{ marginRight: 8 }} />
                  {monthData.monthName}
                </Title>
                
                <div style={{ marginTop: 8, padding: '8px', backgroundColor: '#f9f9f9' }}>
                  <Row gutter={16}>
                    <Col span={6}>
                      <Statistic
                        title="Объем продаж"
                        value={formatNumber(monthData.totalSales)}
                        suffix={salesReportData.currency}
                      />
                    </Col>
                    <Col span={6}>
                      <Statistic
                        title="Профит"
                        value={formatNumber(monthData.totalProfit)}
                        suffix={salesReportData.currency}
                      />
                    </Col>
                    <Col span={6}>
                      <Statistic
                        title="Количество"
                        value={formatNumber(monthData.totalQuantity)}
                        suffix={salesReportData.produce.unitOfMeasurement}
                      />
                    </Col>
                    <Col span={6}>
                      <Statistic
                        title="Продаж"
                        value={monthData.salesCount}
                        suffix="шт"
                      />
                    </Col>
                  </Row>
                  <Row gutter={16} style={{ marginTop: 8 }}>
                    <Col span={8}>
                      <Statistic
                        title="Средняя цена"
                        value={formatNumber(monthData.averagePrice)}
                        suffix={salesReportData.currency}
                      />
                    </Col>
                    <Col span={8}>
                      <Statistic
                        title="Маржа прибыли"
                        value={formatNumber(monthData.profitMargin)}
                        suffix="%"
                      />
                    </Col>
                    <Col span={8}>
                      <Statistic
                        title="Количество (кг)"
                        value={formatNumber(monthData.totalQuantityKg)}
                        suffix="кг"
                      />
                    </Col>
                  </Row>
                </div>
              </Card>
            ))}

            {/* Топ-продажи */}
            {salesReportData.topSales.length > 0 && (
              <Card style={{ marginBottom: 16 }}>
                <Title level={4}>
                  <TrophyOutlined style={{ marginRight: 8 }} />
                  Топ-продажи
                  <Tooltip title="Список самых крупных заказов по объему продаж. Показывает наиболее значимые сделки за период">
                    <InfoCircleOutlined style={{ marginLeft: 8, color: '#1890ff' }} />
                  </Tooltip>
                </Title>
                <Table
                  dataSource={salesReportData.topSales}
                  pagination={false}
                  size="small"
                  columns={[
                    {
                      title: '№',
                      key: 'index',
                      width: 60,
                      render: (_: any, __: any, index: number) => index + 1
                    },
                    {
                      title: 'Чек',
                      dataIndex: 'receipt',
                      key: 'receipt',
                      width: 120
                    },
                    {
                      title: 'Клиент',
                      dataIndex: 'clientName',
                      key: 'clientName',
                      width: 150
                    },
                    {
                      title: 'Дата',
                      dataIndex: 'date',
                      key: 'date',
                      width: 120
                    },
                    {
                      title: 'Количество',
                      dataIndex: 'quantity',
                      key: 'quantity',
                      width: 100,
                      render: (value: number) => `${formatNumber(value)} ${salesReportData.produce.unitOfMeasurement}`
                    },
                    {
                      title: 'Сумма',
                      dataIndex: 'totalSales',
                      key: 'totalSales',
                      width: 120,
                      render: (value: number) => `${formatNumber(value)} ${salesReportData.currency}`
                    },
                    {
                      title: 'Профит',
                      dataIndex: 'profit',
                      key: 'profit',
                      width: 120,
                      render: (value: number) => `${formatNumber(value)} ${salesReportData.currency}`
                    },
                    {
                      title: 'Цена',
                      dataIndex: 'price',
                      key: 'price',
                      width: 100,
                      render: (value: number) => `${formatNumber(value)} ${salesReportData.currency}`
                    },
                    {
                      title: 'Скидка',
                      dataIndex: 'discount',
                      key: 'discount',
                      width: 80,
                      render: (value: number) => `${value}%`
                    }
                  ]}
                  scroll={{ x: 1000 }}
                />
              </Card>
            )}
          </div>
        ) : (
          <div style={{ textAlign: 'center', padding: 32, color: '#888' }}>
            Выберите период, валюту и нажмите "Получить отчет"
          </div>
        )}
      </Modal>

      {/* Модальное окно для изменения количества кг */}
      <Modal
        title={`Изменить количество км - ${selectedProduceForQuantityKg?.name || ""}`}
        open={isQuantityKgModalOpen}
        onCancel={handleCloseQuantityKgModal}
        footer={null}
        width={500}
      >
        <Form
          form={quantityKgForm}
          layout="vertical"
          onFinish={handleQuantityKgSubmit}
        >
          <Form.Item
            label="Новое количество км"
            name="quantityKg"
            rules={[
              { required: true, message: "Введите новое количество км" },
              { type: "number", min: 0, message: "Количество не может быть отрицательным" },
            ]}
          >
            <InputNumber
              style={{ width: "100%" }}
              placeholder="Введите новое количество км"
              precision={3}
            />
          </Form.Item>

          <Form.Item
            label="Причина изменения"
            name="reason"
            rules={[
              { required: true, message: "Укажите причину изменения" },
              { min: 3, message: "Причина должна содержать минимум 3 символа" },
            ]}
          >
            <Input.TextArea
              placeholder="Укажите причину изменения количества км"
              rows={3}
            />
          </Form.Item>

          <Form.Item>
            <Space>
              <Button
                type="primary"
                htmlType="submit"
                loading={quantityKgLoading}
              >
                Сохранить
              </Button>
              <Button onClick={handleCloseQuantityKgModal}>
                Отмена
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}
